import React, { useCallback, useState } from "react";
import { t, Trans } from "@lingui/macro";
import { useWeb3React } from "@web3-react/core";

import Tooltip from "components/Tooltip/Tooltip";

import Vault from "abis/Vault.json";
import ReaderV2 from "abis/ReaderV2.json";
import RewardReader from "abis/RewardReader.json";
import Token from "abis/Token.json";
import ClpManager from "abis/ClpManager.json";

import { ethers } from "ethers";
import {
  CLP_DECIMALS,
  getBalanceAndSupplyData,
  getDepositBalanceData,
  getProcessedData,
  getStakingData,
  getVestingData,
  PLACEHOLDER_ACCOUNT,
  USD_DECIMALS,
} from "lib/legacy";
import { useCadPrice } from "domain/legacy";
import { CANTO, getChainName, getConstant } from "config/chains";

import useSWR from "swr";

import { getContract } from "config/contracts";

import "./StakeV2.css";
import { getServerUrl } from "config/backend";
import { contractFetcher } from "lib/contracts";
import { helperToast } from "lib/helperToast";
import { bigNumberify, formatAmount, formatKeyAmount } from "lib/numbers";
import { useChainId } from "lib/chains";
import ExternalLink from "components/ExternalLink/ExternalLink";
import { StakingTotalRewards } from "../../components/StakingTotalRewards/StakingTotalRewards";
import { Wrapper } from "../../components/Wrapper/Wrapper";
import classes from "./StakeStyles.module.scss";
import { StakingCard } from "../../components/StakingCard/StakingCard";
import { uniqueId } from "lodash";
import EsCadIcon from "../../img/esCadIcon-min.png";
import { useHistory } from "react-router-dom";
import classNames from "classnames";
import { VestingCard } from "../../components/VestingCard/VestingCard";
import CADAprTooltip from "../../components/Stake/CADAprTooltip";
import { StakeModal } from "../../components/Modals/StakeModal/StakeModal";
import { UnstakeModal } from "../../components/Modals/UnstakeModal/UnstakeModal";
import { STAKING_INSURANCE, VESTING_REWARDS } from "../../constants/links";
import { CompoundModal } from "../../components/Modals/CompoundModal/CompoundModal";
import { ClaimModal } from "../../components/Modals/ClaimModal/ClaimModal";
import { VesterDepositModal } from "../../components/Modals/VesterDepositModal/VesterDepositModal";
import { VesterWithdrawModal } from "../../components/Modals/VesterWithdrawModal/VesterWithdrawModal";
import ClpSwap from "../../components/Clp/ClpSwap";
import CadIcon from "../../img/CAD_logo.svg";
import ClpIcon from "../../img/clpIcon.png";
import InfoIcon from "../../img/info.png";
import { StakeCardDivider } from "./styles";
import StatsTooltipRow from "../../components/StatsTooltip/StatsTooltipRow";
import Button from "../../components/Button/Button";

const { AddressZero } = ethers.constants;

export default function StakeV2({ setPendingTxns, connectWallet }) {
  const { active, library, account } = useWeb3React();
  const { chainId } = useChainId();

  const chainName = getChainName(chainId);

  const history = useHistory();

  const hasInsurance = false;
  const [swapModalProps, setSwapModalProps] = useState(null);
  const [stakeModalProps, setStakeModalProps] = useState(null);
  const [unstakeModalProps, setUnstakeModalProps] = useState(null);
  const [vesterDepositModalProps, setVesterDepositModalProps] = useState(null);
  const [vesterWithdrawModalProps, setVesterWithdrawModalProps] = useState(null);
  const [isCompoundModalVisible, setIsCompoundModalVisible] = useState(false);
  const [isClaimModalVisible, setIsClaimModalVisible] = useState(false);

  const rewardRouterAddress = getContract(chainId, "RewardRouter");
  const rewardReaderAddress = getContract(chainId, "RewardReader");
  const readerAddress = getContract(chainId, "Reader");

  const vaultAddress = getContract(chainId, "Vault");
  const nativeTokenAddress = getContract(chainId, "NATIVE_TOKEN");
  const cadAddress = getContract(chainId, "CAD");
  const esCadAddress = getContract(chainId, "ES_CAD");
  const bnCadAddress = getContract(chainId, "BN_CAD");
  const clpAddress = getContract(chainId, "CLP");

  const stakedCadTrackerAddress = getContract(chainId, "StakedCadTracker");
  const bonusCadTrackerAddress = getContract(chainId, "BonusCadTracker");
  const feeCadTrackerAddress = getContract(chainId, "FeeCadTracker");

  const stakedClpTrackerAddress = getContract(chainId, "StakedClpTracker");
  const feeClpTrackerAddress = getContract(chainId, "FeeClpTracker");

  const clpManagerAddress = getContract(chainId, "ClpManager");

  const stakedCadDistributorAddress = getContract(chainId, "StakedCadDistributor");
  const stakedClpDistributorAddress = getContract(chainId, "StakedClpDistributor");

  const cadVesterAddress = getContract(chainId, "CadVester");
  const clpVesterAddress = getContract(chainId, "ClpVester");

  const vesterAddresses = [cadVesterAddress, clpVesterAddress];

  const excludedEsCadAccounts = [stakedCadDistributorAddress, stakedClpDistributorAddress];

  const nativeTokenSymbol = getConstant(chainId, "nativeTokenSymbol");
  const wrappedTokenSymbol = getConstant(chainId, "wrappedTokenSymbol");

  const walletTokens = [cadAddress, esCadAddress, clpAddress, stakedCadTrackerAddress];
  const depositTokens = [
    cadAddress,
    esCadAddress,
    stakedCadTrackerAddress,
    bonusCadTrackerAddress,
    bnCadAddress,
    clpAddress,
  ];
  const rewardTrackersForDepositBalances = [
    stakedCadTrackerAddress,
    stakedCadTrackerAddress,
    bonusCadTrackerAddress,
    feeCadTrackerAddress,
    feeCadTrackerAddress,
    feeClpTrackerAddress,
  ];
  const rewardTrackersForStakingInfo = [
    stakedCadTrackerAddress,
    bonusCadTrackerAddress,
    feeCadTrackerAddress,
    stakedClpTrackerAddress,
    feeClpTrackerAddress,
  ];

  const { data: walletBalances } = useSWR(
    [
      `StakeV2:walletBalances:${active}`,
      chainId,
      readerAddress,
      "getTokenBalancesWithSupplies",
      account || PLACEHOLDER_ACCOUNT,
    ],
    {
      fetcher: contractFetcher(library, ReaderV2, [walletTokens]),
    }
  );

  const { data: depositBalances } = useSWR(
    [
      `StakeV2:depositBalances:${active}`,
      chainId,
      rewardReaderAddress,
      "getDepositBalances",
      account || PLACEHOLDER_ACCOUNT,
    ],
    {
      fetcher: contractFetcher(library, RewardReader, [depositTokens, rewardTrackersForDepositBalances]),
    }
  );

  const { data: stakingInfo } = useSWR(
    [`StakeV2:stakingInfo:${active}`, chainId, rewardReaderAddress, "getStakingInfo", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: contractFetcher(library, RewardReader, [rewardTrackersForStakingInfo]),
    }
  );

  const { data: stakedCadSupply } = useSWR(
    [`StakeV2:stakedCadSupply:${active}`, chainId, cadAddress, "balanceOf", stakedCadTrackerAddress],
    {
      fetcher: contractFetcher(library, Token),
    }
  );

  const { data: aums } = useSWR([`StakeV2:getAums:${active}`, chainId, clpManagerAddress, "getAums"], {
    fetcher: contractFetcher(library, ClpManager),
  });

  const { data: nativeTokenPrice } = useSWR(
    [`StakeV2:nativeTokenPrice:${active}`, chainId, vaultAddress, "getMinPrice", nativeTokenAddress],
    {
      fetcher: contractFetcher(library, Vault),
    }
  );

  const { data: esCadSupply } = useSWR(
    [`StakeV2:esCadSupply:${active}`, chainId, readerAddress, "getTokenSupply", esCadAddress],
    {
      fetcher: contractFetcher(library, ReaderV2, [excludedEsCadAccounts]),
    }
  );

  const { data: vestingInfo } = useSWR(
    [`StakeV2:vestingInfo:${active}`, chainId, readerAddress, "getVestingInfo", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: contractFetcher(library, ReaderV2, [vesterAddresses]),
    }
  );

  const { cadPrice } = useCadPrice(chainId, { canto: chainId === CANTO ? library : undefined }, active);

  const cadSupplyUrl = getServerUrl(chainId, "/api/cad_supply");
  const { data: cadSupply } = useSWR([cadSupplyUrl], {
    fetcher: (...args) => fetch(...args).then((res) => res.text()),
  });

  const isCadTransferEnabled = true;

  let aum;
  if (aums && aums.length > 0) {
    aum = aums[0].add(aums[1]).div(2);
  }

  const { balanceData, supplyData } = getBalanceAndSupplyData(walletBalances);

  const depositBalanceData = getDepositBalanceData(depositBalances);
  const stakingData = getStakingData(stakingInfo);
  const vestingData = getVestingData(vestingInfo);

  const processedData = getProcessedData(
    balanceData,
    supplyData,
    depositBalanceData,
    stakingData,
    vestingData,
    aum,
    nativeTokenPrice,
    stakedCadSupply,
    cadPrice,
    cadSupply
  );

  let multiplierPointsAmount;
  if (processedData && processedData.bonusCadTrackerRewards && processedData.bnCadInFeeCad) {
    multiplierPointsAmount = processedData.bonusCadTrackerRewards.add(processedData.bnCadInFeeCad);
  }

  let totalRewardTokens;
  if (processedData && processedData.bnCadInFeeCad && processedData.bonusCadInFeeCad) {
    totalRewardTokens = processedData.bnCadInFeeCad.add(processedData.bonusCadInFeeCad);
  }

  const bonusCadInFeeCad = processedData ? processedData.bonusCadInFeeCad : undefined;

  let maxUnstakeableCad = bigNumberify(0);
  if (
    totalRewardTokens &&
    vestingData &&
    vestingData.cadVesterPairAmount &&
    multiplierPointsAmount &&
    processedData.bonusCadInFeeCad
  ) {
    const availableTokens = totalRewardTokens.sub(vestingData.cadVesterPairAmount);
    const stakedTokens = processedData.bonusCadInFeeCad;
    const divisor = multiplierPointsAmount.add(stakedTokens);
    if (divisor.gt(0)) {
      maxUnstakeableCad = availableTokens.mul(stakedTokens).div(divisor);
    }
  }

  const showStakeCadModal = () => {
    if (!isCadTransferEnabled) {
      helperToast.error(t`CAD transfers not yet enabled`);
      return;
    }

    setStakeModalProps({
      title: "Stake CAD",
      maxAmount: processedData.cadBalance,
      stakingTokenSymbol: "CAD",
      stakingTokenAddress: cadAddress,
      farmAddress: stakedCadTrackerAddress,
      stakeMethodName: "stakeCad",
    });
  };

  const showStakeEsCadModal = () => {
    setStakeModalProps({
      title: "Stake esCAD",
      maxAmount: processedData.esCadBalance,
      tokenSymbol: "esCAD",
      stakingTokenAddress: esCadAddress,
      farmAddress: AddressZero,
      stakeMethodName: "stakeEsCad",
    });
  };

  const showCadVesterDepositModal = () => {
    let remainingVestableAmount = vestingData.cadVester.maxVestableAmount.sub(vestingData.cadVester.vestedAmount);
    if (processedData && processedData.esCadBalance && processedData.esCadBalance.lt(remainingVestableAmount)) {
      remainingVestableAmount = processedData.esCadBalance;
    }

    setVesterDepositModalProps({
      title: `CAD Vault`,
      stakeTokenLabel: "staked CAD + esCAD + Multiplier Points",
      maxAmount: remainingVestableAmount,
      balance: processedData.esCadBalance,
      // escrowedBalance: vestingData.cadVester.escrowedBalance,
      vestedAmount: vestingData.cadVester.vestedAmount,
      maxVestableAmount: vestingData.cadVester.maxVestableAmount,
      averageStakedAmount: vestingData.cadVester.averageStakedAmount,
      reserveAmount: vestingData.cadVester.pairAmount,
      maxReserveAmount: totalRewardTokens,
      vesterAddress: cadVesterAddress,
    });
  };

  const showClpVesterDepositModal = () => {
    let remainingVestableAmount = vestingData.clpVester.maxVestableAmount.sub(vestingData.clpVester.vestedAmount);
    if (processedData && processedData.esCadBalance && processedData.esCadBalance.lt(remainingVestableAmount)) {
      remainingVestableAmount = processedData.esCadBalance;
    }

    setVesterDepositModalProps({
      title: "CLP Vault",
      stakeTokenLabel: "staked CLP",
      maxAmount: remainingVestableAmount,
      balance: processedData.esCadBalance,
      // escrowedBalance: vestingData.clpVester.escrowedBalance,
      vestedAmount: vestingData.clpVester.vestedAmount,
      maxVestableAmount: vestingData.clpVester.maxVestableAmount,
      averageStakedAmount: vestingData.clpVester.averageStakedAmount,
      reserveAmount: vestingData.clpVester.pairAmount,
      maxReserveAmount: processedData.clpBalance,
      vesterAddress: clpVesterAddress,
    });
  };

  const showCadVesterWithdrawModal = () => {
    if (!vestingData || !vestingData.cadVesterVestedAmount || vestingData.cadVesterVestedAmount.eq(0)) {
      helperToast.error(t`You have not deposited any tokens for vesting.`);
      return;
    }

    setVesterWithdrawModalProps({
      title: `Withdraw from CAD Vault`,
      vesterAddress: cadVesterAddress,
    });
  };

  const showClpVesterWithdrawModal = () => {
    if (!vestingData || !vestingData.clpVesterVestedAmount || vestingData.clpVesterVestedAmount.eq(0)) {
      helperToast.error(t`You have not deposited any tokens for vesting.`);
      return;
    }

    setVesterWithdrawModalProps({
      title: `Withdraw from CLP Vault`,
      vesterAddress: clpVesterAddress,
    });
  };

  const showUnstakeCadModal = () => {
    if (!isCadTransferEnabled) {
      helperToast.error(t`CAD transfers not yet enabled`);
      return;
    }

    let maxAmount = processedData.cadInStakedCad;
    if (
      processedData.cadInStakedCad &&
      vestingData &&
      vestingData.cadVesterPairAmount.gt(0) &&
      maxUnstakeableCad &&
      maxUnstakeableCad.lt(processedData.cadInStakedCad)
    ) {
      maxAmount = maxUnstakeableCad;
    }

    setUnstakeModalProps({
      title: `Unstake CAD`,
      maxAmount: maxAmount,
      reservedAmount: vestingData.cadVesterPairAmount,
      unstakingTokenSymbol: "CAD",
      unstakeMethodName: "unstakeCad",
    });
  };

  const showUnstakeEsCadModal = () => {
    let maxAmount = processedData.esCadInStakedCad;
    if (
      processedData.esCadInStakedCad &&
      vestingData &&
      vestingData.cadVesterPairAmount.gt(0) &&
      maxUnstakeableCad &&
      maxUnstakeableCad.lt(processedData.esCadInStakedCad)
    ) {
      maxAmount = maxUnstakeableCad;
    }

    setUnstakeModalProps({
      title: `Unstake esCAD`,
      maxAmount: maxAmount,
      reservedAmount: vestingData.cadVesterPairAmount,
      unstakingTokenSymbol: "esCAD",
      unstakeMethodName: "unstakeEsCad",
    });
  };

  const renderMultiplierPointsValue = useCallback(() => {
    return (
      <Tooltip
        handle={`100.00%`}
        position="right-bottom"
        renderContent={() => {
          return (
            <Trans>
              Boost your rewards with <ExternalLink href={VESTING_REWARDS}>Multiplier Points.</ExternalLink>
            </Trans>
          );
        }}
      />
    );
  }, []);

  const cadApr = (
    <Tooltip
      // handle={`${formatKeyAmount(processedData, "cadAprTotalWithBoost", 2, 2, true)}%`}
      // position="right-bottom"
      // renderContent={() => <CADAprTooltip processedData={processedData} nativeTokenSymbol={nativeTokenSymbol} />}
    />
  );

  const esCadApr = (
    <Tooltip
      // handle={`${formatKeyAmount(processedData, "cadAprTotalWithBoost", 2, 2, true)}%`}
      // position="right-bottom"
      // renderContent={() => <CADAprTooltip processedData={processedData} nativeTokenSymbol={nativeTokenSymbol} />}
    />
  );

  const clpApr = (
    <Tooltip
      // handle={`${formatKeyAmount(processedData, "clpAprTotal", 2, 2, true)}%`}
      position="right-bottom"
      renderContent={() => {
        return (
          <>
            {processedData?.clpAprForNativeToken?.gt(0) && (
              <StatsTooltipRow
                label={`${nativeTokenSymbol} APR`}
                value={`${formatKeyAmount(processedData, "clpAprForNativeToken", 2, 2, true)}%`}
                showDollar={false}
              />
            )}
            {processedData?.clpAprForEsCad?.gt(0) && (
              <StatsTooltipRow
                label="Escrowed CAD APR"
                value={`${formatKeyAmount(processedData, "clpAprForEsCad", 2, 2, true)}%`}
                showDollar={false}
              />
            )}

            <br />

            <Trans>
              {/* APRs are updated weekly on Wednesday and will depend on the fees collected for the week. <br />
              <br /> */}
              {/* Historical CLP APRs can be checked in this{" "}
            <ExternalLink href="https://dune.com/saulius/cad-analytics">
              community dashboard
            </ExternalLink> */}
              {/* . */}
            </Trans>
          </>
        );
      }}
    />
  );

  const cadBoost = (
    <Tooltip
      handle={`${formatAmount(processedData.boostBasisPoints, 2, 2, false)}%`}
      position="right-bottom"
      renderContent={() => {
        return (
          <div>
            <Trans>
              You are earning {formatAmount(processedData.boostBasisPoints, 2, 2, false)}% more {nativeTokenSymbol}{" "}
              rewards using {formatAmount(processedData.bnCadInFeeCad, 18, 4, 2, true)} Staked Multiplier Points.
            </Trans>
            <br />
            <br />
            <Trans>Use the "Compound" button to stake your Multiplier Points.</Trans>
          </div>
        );
      }}
    />
  );

  const cadInfo = (
    <Tooltip
      handle="CAD"
      position="right-bottom"
      renderContent={() => {
        return (
          <div>
            <Trans>$CAD is the platform's utility and governance token</Trans>
            <br />
            <br />
            <Trans>
              {" "}
              <ExternalLink
                href={
                  "https://cadenceprotocol.gitbook.io/cadence-protocol/introduction-to-cadence/cad-token-and-escad-staking"
                }
              >
                Learn More.
              </ExternalLink>
            </Trans>
          </div>
        );
      }}
    />
  );

  const esCadInfo = (
    <Tooltip
      handle="Escrowed CAD"
      position="right-bottom"
      renderContent={() => {
        return (
          <div>
            <Trans>esCAD can be staked for rewards, similar to regular CAD tokens.</Trans>
            <br />
            <br />
            <Trans>
              esCAD tokens can be converted to CAD tokens by vesting them in the Vault. They unlock every second over a
              1 year period
            </Trans>
            <br />
            <br />
            <Trans>
              {" "}
              <ExternalLink
                href={"https://cadenceprotocol.gitbook.io/cadence-protocol/achievements-and-rewards/vesting-rewards"}
              >
                Learn More.
              </ExternalLink>
            </Trans>
          </div>
        );
      }}
    />
  );

  const clpInfo = (
    <Tooltip
      handle="CLP"
      position="right-bottom"
      renderContent={() => {
        return (
          <div>
            <Trans>
              CLP is Cadence's liquidity token. The liquidity provided is used towards perpetuals trading and swaps on
              Cadence
            </Trans>
            <br />
            <br />
            <Trans>CLP holders accrue esCAD, multiplier points, and 70% of all fees generated</Trans>
            <br />
            <br />
            <Trans>
              {" "}
              <ExternalLink
                href={"https://cadenceprotocol.gitbook.io/cadence-protocol/trading-on-cadence-protocol/clp"}
              >
                Learn More.
              </ExternalLink>
            </Trans>
          </div>
        );
      }}
    />
  );

  const cadVaultInfo = (
    <Tooltip
      handle="CAD Vault"
      position="right-bottom"
      renderContent={() => {
        return (
          <div>
            <Trans>Use this Vault to convert esCAD earned from Staking CAD into CAD.</Trans>
            <br />
            <br />
            <Trans>
              {" "}
              <ExternalLink
                href={"https://cadenceprotocol.gitbook.io/cadence-protocol/achievements-and-rewards/vesting-rewards"}
              >
                Learn More.
              </ExternalLink>
            </Trans>
          </div>
        );
      }}
    />
  );

  const clpVaultInfo = (
    <Tooltip
      handle="CLP Vault"
      position="right-bottom"
      renderContent={() => {
        return (
          <div>
            <Trans>Use this Vault to convert esCAD earned from CLP into CAD.</Trans>
            <br />
            <br />
            <Trans>
              {" "}
              <ExternalLink
                href={"https://cadenceprotocol.gitbook.io/cadence-protocol/achievements-and-rewards/vesting-rewards"}
              >
                Learn More.
              </ExternalLink>
            </Trans>
          </div>
        );
      }}
    />
  );

  const coinBalances = [
    {
      title: wrappedTokenSymbol,
      balance: formatKeyAmount(processedData, "totalNativeTokenRewards", 18, 4, false),
      balanceUsd: `$ ${formatKeyAmount(processedData, "totalNativeTokenRewardsUsd", USD_DECIMALS, 2, true)}`,
      color: "#1FED88",
    },
    {
      title: "CAD",
      balance: formatKeyAmount(processedData, "totalVesterRewards", 18, 4, false),
      balanceUsd: `$ ${formatKeyAmount(processedData, "totalVesterRewardsUsd", USD_DECIMALS, 2, true)}`,
      color: "#FE3D4E",
    },
    {
      title: "EsCAD",
      balance: formatKeyAmount(processedData, "totalEsCadRewards", 18, 4, false),
      balanceUsd: `$ ${formatKeyAmount(processedData, "totalEsCadRewardsUsd", USD_DECIMALS, 2, true)}`,
      color: "#01A0FA",
    },
  ];

  const cardsData = [
    {
      title: "CAD",
      symbol: "CAD",
      icon: CadIcon,
      infoicon: InfoIcon,
      apr: cadApr,
      price: formatAmount(cadPrice, USD_DECIMALS, 2, true),
      rewardsUsd: formatKeyAmount(processedData, "totalCadRewardsUsd", USD_DECIMALS, 2, true),
      multiplierPointsAPR: renderMultiplierPointsValue(),
      boostPercentage: cadBoost,
      walletBalance: formatKeyAmount(processedData, "cadBalance", 18, 2, true),
      staked: formatKeyAmount(processedData, "cadInStakedCad", 18, 2, true),
      stakedInUsd: formatKeyAmount(processedData, "cadInStakedCadUsd", USD_DECIMALS, 2, true),
      onStakeClick: active ? () => showStakeCadModal() : undefined,
      onUnStakeClick: active ? () => showUnstakeCadModal() : undefined,
      onBuyClick: () => (window.location.href = "https://app.slingshot.finance/swap/Canto/WCANTO"),
      onStakeCLPClick: null,
      onUnStakeCLPClick: null,
    },
    {
      title: esCadInfo,
      symbol: "EsCAD",
      icon: EsCadIcon,
      infoicon: InfoIcon,
      price: formatKeyAmount(processedData, "clpPrice", USD_DECIMALS, 3, true),
      apr: esCadApr,
      multiplierPointsAPR: renderMultiplierPointsValue(),
      walletBalance: formatKeyAmount(processedData, "esCadBalance", 18, 2, true),
      staked: formatKeyAmount(processedData, "esCadInStakedCad", 18, 2, true),
      stakedInUsd: formatKeyAmount(processedData, "esCadInStakedCadUsd", USD_DECIMALS, 2, true),
      onStakeClick: active ? () => showStakeEsCadModal() : undefined,
      onUnStakeClick: active ? () => showUnstakeEsCadModal() : undefined,
      onStakeCLPClick: null,
      onUnStakeCLPClick: null,
    },
  ];

  const clpCardsData = [
    {
      title: clpInfo,
      symbol: `CLP`,
      icon: ClpIcon,
      infoicon: InfoIcon,
      price: formatKeyAmount(processedData, "clpPrice", USD_DECIMALS, 3, true),
      apr: clpApr,
      rewardsUsd: formatKeyAmount(processedData, "totalClpRewardsUsd", USD_DECIMALS, 2, true),
      walletBalance: formatKeyAmount(processedData, "clpBalance", 18, 2, true),
      staked: formatKeyAmount(processedData, "clpBalance", CLP_DECIMALS, 2, true),
      stakedInUsd: formatKeyAmount(processedData, "clpBalanceUsd", USD_DECIMALS, 2, true),
      onStakeClick: null,
      onUnStakeClick: null,
      // onStakeCLPClick: active
      //   ? () => {
      //       setSwapModalProps({
      //         presetIsBuying: true,
      //         setPendingTxns,
      //         onClose: () => setSwapModalProps(null),
      //       });
      //     }
      //   : undefined,
      onUnStakeCLPClick: active
        ? () => {
            setSwapModalProps({
              presetIsBuying: false,
              setPendingTxns,
              onClose: () => setSwapModalProps(null),
            });
          }
        : undefined,
      onInsuranceClick: hasInsurance ? () => (window.location.href = STAKING_INSURANCE) : undefined,
    },
  ];

  const vestingCards = [
    {
      icon: CadIcon,
      title: cadVaultInfo,
      vestingStartValue: formatKeyAmount(vestingData, "cadVesterClaimSum", 18, 4, false),
      vestingEndValue: formatKeyAmount(vestingData, "cadVesterVestedAmount", 18, 4, false),
      symbol: "CAD",
      esSymbol: "esCAD",
      availableToClaim: formatKeyAmount(vestingData, "cadVesterClaimable", 18, 4, true),
      onDepositClick: () => showCadVesterDepositModal(),
      onWithdrawClick: () => showCadVesterWithdrawModal(),
    },
    {
      icon: ClpIcon,
      title: clpVaultInfo,
      vestingStartValue: formatKeyAmount(vestingData, "clpVesterClaimSum", 18, 4, false),
      vestingEndValue: formatKeyAmount(vestingData, "clpVesterVestedAmount", 18, 4, false),
      symbol: "CAD",
      esSymbol: "esCAD",
      availableToClaim: formatKeyAmount(vestingData, "clpVesterClaimable", 18, 4, true),
      onDepositClick: () => showClpVesterDepositModal(),
      onWithdrawClick: () => showClpVesterWithdrawModal(),
    },
  ];

  return (
    <Wrapper className={classes.wrapper}>
      <StakingTotalRewards
        multiplierPoints={formatKeyAmount(processedData, "bonusCadTrackerRewards", 18, 4, true)}
        stakedMultiplierPoints={formatKeyAmount(processedData, "bnCadInFeeCad", 18, 4, true)}
        coinsBalances={coinBalances}
        totalBalance={`$ ${formatKeyAmount(processedData, "totalRewardsUsd", USD_DECIMALS, 2, true)}`}
        onCompoundClick={() => setIsCompoundModalVisible(true)}
        onClaimClick={() => setIsClaimModalVisible(true)}
        isAuthorized={active}
        connectWallet={connectWallet}
      />

      <section className={classNames(classes.sectionWrapper, classes.stakingSection)}>
        <div className={classes.titleWrapper}>
          <h2 className={classes.sectionTitle}>
            <Trans>Stake</Trans>
          </h2>
          <span className={classes.sectionDescription}>
            <Trans>Stake CAD and esCAD to earn esCAD, Multiplier Points, and CANTO fee revenue</Trans>
          </span>
        </div>
        <div className={classes.stakeCards}>
          {cardsData.map((card) => (
            <StakingCard key={uniqueId(card.symbol)} isAuthorized={active} connectWallet={connectWallet} {...card} />
          ))}
        </div>
      </section>
      <section className={classNames(classes.sectionWrapper, classes.stakingSection)}>
        <div className={classes.titleWrapper}>
          <h2 className={classes.sectionTitle}>
            <Trans>Provide Liquidity</Trans>
          </h2>
          <span className={classes.sectionDescription}>
            <Trans>Provide liquidity to earn esCAD and CANTO fee revenue.</Trans>
          </span>
        </div>
        <div className={classes.stakeCards}>
          {clpCardsData.map((card) => (
            <StakingCard key={uniqueId(card.symbol)} isAuthorized={active} connectWallet={connectWallet} {...card} />
          ))}
        </div>
      </section>

      <section className={classNames(classes.sectionWrapper, classes.vestingSection)}>
        <div className={classes.titleWrapper}>
          <h2 className={classes.sectionTitle}>
            <Trans>Vest</Trans>
          </h2>
          <span className={classes.sectionDescription}>
            <Trans>Convert esCAD to CAD. Please read the vesting details before using the vaults. </Trans>
          </span>
        </div>
        <div className={classes.vestingCards}>
          {vestingCards.map((card) => (
            <VestingCard key={uniqueId(card.title)} isAuthorized={active} connectWallet={connectWallet} {...card} />
          ))}
        </div>
      </section>

      {stakeModalProps ? (
        <StakeModal
          onClose={() => setStakeModalProps(null)}
          rewardRouterAddress={rewardRouterAddress}
          setPendingTxns={setPendingTxns}
          {...stakeModalProps}
        />
      ) : (
        <></>
      )}

      {unstakeModalProps ? (
        <UnstakeModal
          setPendingTxns={setPendingTxns}
          onClose={() => setUnstakeModalProps(null)}
          multiplierPointsAmount={multiplierPointsAmount}
          bonusCadInFeeCad={bonusCadInFeeCad}
          rewardRouterAddress={rewardRouterAddress}
          {...unstakeModalProps}
        />
      ) : (
        <></>
      )}

      {vesterDepositModalProps ? (
        <VesterDepositModal
          onClose={() => setVesterDepositModalProps(null)}
          setPendingTxns={setPendingTxns}
          {...vesterDepositModalProps}
        />
      ) : (
        <></>
      )}

      {vesterWithdrawModalProps ? (
        <VesterWithdrawModal
          onClose={() => setVesterWithdrawModalProps(null)}
          setPendingTxns={setPendingTxns}
          {...vesterWithdrawModalProps}
        />
      ) : (
        <></>
      )}

      {isCompoundModalVisible ? (
        <CompoundModal
          setPendingTxns={setPendingTxns}
          onClose={() => setIsCompoundModalVisible(false)}
          rewardRouterAddress={rewardRouterAddress}
          totalVesterRewards={processedData.totalVesterRewards}
          wrappedTokenSymbol={wrappedTokenSymbol}
          nativeTokenSymbol={nativeTokenSymbol}
        />
      ) : (
        <></>
      )}

      {isClaimModalVisible ? (
        <ClaimModal
          onClose={() => setIsClaimModalVisible(false)}
          rewardRouterAddress={rewardRouterAddress}
          setPendingTxns={setPendingTxns}
          nativeTokenSymbol={nativeTokenSymbol}
          wrappedTokenSymbol={wrappedTokenSymbol}
        />
      ) : (
        <></>
      )}

      {swapModalProps ? <ClpSwap {...swapModalProps} /> : <></>}
    </Wrapper>
  );
}
